// Shared pure helpers: pass-rate coloring, prior-window derivation,
// view-as filter markers, scorecard name lookup, and the scored-calls
// formatting/tier math + pills.
import React, { useMemo } from 'react'
import { isNil, isEmpty } from 'lodash'
import { useSelector } from 'react-redux'
import moment from 'moment'

import { Pill } from '@/components/pills/Pill'

import { CHART } from '@/components/charts/chartPalette'

// ---- escapeHtml ----
// ECharts renders CUSTOM string tooltip formatters as raw HTML (only
// the default formatter escapes), so every user-controlled name
// (agent/criterion/scorecard/tag) interpolated into tooltip markup
// must pass through here or an adversarial name executes as HTML in
// every viewer's browser.
const HTML_ESCAPES = { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }
export const escapeHtml = (value) =>
  isNil(value) ? '' : String(value).replace(/[&<>"']/g, (ch) => HTML_ESCAPES[ch])

// ---- daily rollups ----
// Rollups over the by_date response shape ({aggregates, calls}): the
// KPI row and the entity drawers both need "total calls" and the
// call-weighted average score for a date window.
export const sumDailyCalls = (calls = []) =>
  calls.reduce((total, row) => total + (parseInt(row?.y, 10) || 0), 0)

export const weightedAvgScore = (aggregates = [], calls = []) => {
  if (isEmpty(aggregates)) {
    return null
  }
  let weightedSum = 0
  let totalWeight = 0
  aggregates.forEach((row, index) => {
    const score = parseFloat(row?.['QA Score']) || 0
    const weight = parseInt(calls[index]?.y, 10) || 0
    weightedSum += score * weight
    totalWeight += weight
  })
  return totalWeight === 0 ? null : weightedSum / totalWeight
}

// ---- csv ----
// Shared CSV download used by the widget/browser export buttons.
// Fields with commas/quotes/newlines are escaped so the file
// round-trips through Excel.
export const escapeCsv = (value) => {
  const s = isNil(value) ? '' : String(value)
  if (/[",\n]/.test(s)) {
    return `"${s.replace(/"/g, '""')}"`
  }
  return s
}

export const downloadCsvFile = (filename, header, rows) => {
  const csv = [header, ...rows].map((row) => row.map(escapeCsv).join(',')).join('\n')
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' })
  const url = URL.createObjectURL(blob)
  const link = document.createElement('a')
  link.href = url
  link.download = filename
  document.body.appendChild(link)
  link.click()
  document.body.removeChild(link)
  URL.revokeObjectURL(url)
}

// ---- passRateColor ----
// Shared threshold-tier color for a pass rate: red <70, orange 70-80,
// amber 80-90, green 90+ (tracks the improvement/meets/exceeds bands).

export const colorForPassRate = (pct) => {
  if (pct >= 90) {
    return CHART.SUCCESS // exceeds -- green
  }
  if (pct >= 80) {
    return CHART.WARNING // meets -- amber
  }
  if (pct >= 70) {
    return CHART.CAUTION // needs improvement -- orange
  }
  return CHART.CRITICAL // critical -- red
}

// ---- priorWindow ----
// Given a current window [start, end], return the immediately-preceding
// equal-length window whose end matches the current start. Mirrors
// clickhouse_get_agents_period_comparison's default (analytics.py:770-
// 773): `prior_end = filters.start_date`, NOT start - 1ms. Both work
// for daily-bucketed data, but using the same boundary makes the FE
// prior windows byte-identical to BE-defaulted prior windows.
export const derivePriorWindow = (startDate, endDate) => {
  if (!startDate || !endDate) {
    return null
  }
  const start = new Date(startDate).getTime()
  const end = new Date(endDate).getTime()
  if (Number.isNaN(start) || Number.isNaN(end) || end <= start) {
    return null
  }
  const span = end - start
  const priorEnd = start
  const priorStart = priorEnd - span
  return {
    priorStart: new Date(priorStart).toISOString(),
    priorEnd: new Date(priorEnd).toISOString(),
  }
}

// ---- viewAsHelpers ----
// TREN-317 — helpers for "View as agent" mode.
//
// View-as mode rides on top of the existing qa-copilot `filters.agents`
// state (so the dashboard's data fetches automatically scope to the
// single agent). To distinguish it from a regular agent filter applied
// via the filter bar, we tag the entry with `source: VIEW_AS_SOURCE`.

export const VIEW_AS_SOURCE = 'view-as'

// Build the qa-copilot filter value for an agent. The `source` flag
// lets us distinguish "View as agent" mode from a regular agent filter
// applied via the filter bar.
export const buildViewAsAgentFilterValue = (agent) => ({
  value: agent.user_id,
  label: agent.full_name || `Agent ${agent.user_id}`,
  source: VIEW_AS_SOURCE,
})

// True when filters.agents represents a single view-as selection.
export const isViewAsAgentFilter = (agents) =>
  Array.isArray(agents) && agents.length === 1 && agents[0]?.source === VIEW_AS_SOURCE

// ---- useScorecardNameById ----
// Memoized {sid -> scorecard display name} map derived from the
// page-load scorecards list in qa-copilot redux. The cohort-shape
// flatten needs this lookup; it
// was hand-rolled in two places. The map keys default to `value`
// (scorecards as Select options) but tolerate the raw model shape
// too (`sid` / `name`).

export const useScorecardNameById = () => {
  const scorecardOptions = useSelector((state) => state.qaCopilot.data.scorecards)
  return useMemo(() => {
    const map = {}
    scorecardOptions.forEach((option) => {
      const sid = option?.value || option?.sid
      const name = option?.label || option?.name
      if (sid && name) {
        map[sid] = name
      }
    })
    return map
  }, [scorecardOptions])
}

// ---- scoredCallsMath ----
// Pure helpers for ScoredCallsBrowser. Kept separate so the
// tier/filter math can be unit tested without mounting React.
//
// A scored call row from /scoring/scores/clickhouse/calls carries:
//   - aggregate_score / possible_score
//   - improvement_threshold / meets_threshold / exceeds_threshold
//     (0..100 integer knobs straight off the scorecard config -- the
//     BE returns these per row so the FE doesn't need a second fetch)
// `tierForCall` reads thresholds off the row itself. An optional
// `fallback` argument overrides them for legacy callers (tests, the
// previous PG-backed endpoint that didn't carry thresholds inline).
// Defaults to above=90 / below=70 when neither row nor fallback
// supplies a value.

export const TIER = Object.freeze({
  ALL: 'all',
  ABOVE: 'above',
  BELOW: 'below',
})

const DEFAULT_ABOVE = 90
const DEFAULT_BELOW = 70

// Read a threshold value, scaling 0..1 legacy values into 0..100.
// Returns null when the source value is missing.
export const scaleThreshold = (value) => {
  if (isNil(value)) {
    return null
  }
  const numeric = Number(value)
  if (Number.isNaN(numeric)) {
    return null
  }
  return numeric > 1 ? numeric : numeric * 100
}

// Pull thresholds (0..100) for a call: row first, then fallback prop,
// then defaults. Above = exceeds, below = improvement (anything below
// improvement is the failure tier).
const resolveThresholds = (call, fallback) => {
  const above =
    scaleThreshold(call?.exceeds_threshold) ??
    scaleThreshold(fallback?.exceeds_threshold) ??
    DEFAULT_ABOVE
  const below =
    scaleThreshold(call?.improvement_threshold) ??
    scaleThreshold(fallback?.improvement_threshold) ??
    DEFAULT_BELOW
  return { above, below }
}

// Returns the 0..100 percentage for one call, or null when the call
// has no possible_score (or 0 denominator).
export const callPercentage = (call) => {
  if (!call) {
    return null
  }
  const possible = parseFloat(call.possible_score)
  if (!possible || Number.isNaN(possible)) {
    return null
  }
  const aggregate = parseFloat(call.aggregate_score)
  if (Number.isNaN(aggregate)) {
    return null
  }
  return (aggregate / possible) * 100
}

// A call is "scored" only when its scorecard is fully scored. Partially-
// scored / unscored calls (scored_status 'partial' | 'unscored') have no
// final score, so the dashboard shows them as N/A -- matching the old
// Scores page -- rather than a percentage over just the scored criteria.
// scored_status absent (older BE) is treated as scored for back-compat.
export const isCallScored = (call) => isNil(call?.scored_status) || call.scored_status === 'scored'

// 'above' | 'below' | 'middle' | null. null = no score (unscored/partial).
export const tierForCall = (call, fallback) => {
  if (!isCallScored(call)) {
    return null
  }
  const pct = callPercentage(call)
  if (isNil(pct)) {
    return null
  }
  const { above, below } = resolveThresholds(call, fallback)
  if (pct >= above) {
    return 'above'
  }
  if (pct < below) {
    return 'below'
  }
  return 'middle'
}

export const filterCallsByTier = (calls, tier, fallback) => {
  if (!Array.isArray(calls)) {
    return []
  }
  if (tier === TIER.ALL) {
    return calls
  }
  return calls.filter((call) => tierForCall(call, fallback) === tier)
}

export const countCallsByTier = (calls, fallback) => {
  const counts = { all: 0, above: 0, below: 0, middle: 0, unscored: 0 }
  if (!Array.isArray(calls)) {
    return counts
  }
  calls.forEach((call) => {
    counts.all += 1
    const tier = tierForCall(call, fallback)
    if (isNil(tier)) {
      counts.unscored += 1
    } else {
      counts[tier] += 1
    }
  })
  return counts
}

// ---- scoredCallsFormat ----
// Display helpers shared by ScoredCallsBrowser and its sub-panels.

export const formatTimestamp = (isoLike) => {
  if (!isoLike) {
    return '—'
  }
  // Timestamps are UTC. The service marks them with "Z", but parse as UTC
  // regardless: moment() reads an unmarked string as local time.
  const parsed = moment.utc(isoLike).local()
  return parsed.isValid() ? parsed.format('MM/DD HH:mm') : '—'
}

export const durationMinutes = (call) => {
  if (!call?.start_time || !call?.end_time) {
    return null
  }
  const start = moment(call.start_time)
  const end = moment(call.end_time)
  if (!start.isValid() || !end.isValid()) {
    return null
  }
  return Math.max(0, Math.round(end.diff(start, 'minutes', true)))
}

// 90/80/70 tier mapping matches the legacy view_scores page.
// Private -- only ScorePill below consumes this. Was exported when
// this file was split out of ScoredCallsBrowser; no other file ever
// wired it up.
const tierForPct = (pct) => {
  if (isNil(pct) || Number.isNaN(pct)) {
    return null
  }
  if (pct >= 90) {
    return 'exceeds'
  }
  if (pct >= 80) {
    return 'meets'
  }
  if (pct >= 70) {
    return 'improvement'
  }
  return 'failure'
}

// Endpoint returns raw aggregate/possible on every node; this
// computes the percentage and tolerates missing values.
export const pctFrom = (aggregate, possible) => {
  const possibleNum = parseFloat(possible)
  if (!possibleNum || Number.isNaN(possibleNum)) {
    return null
  }
  const aggregateNum = parseFloat(aggregate)
  if (Number.isNaN(aggregateNum)) {
    return null
  }
  return (aggregateNum / possibleNum) * 100
}

// Pass-rate tier -> shared Pill semantic variant. `failure` (< 70%) is
// the critical tier; N/A has no variant and falls back to the neutral
// default Pill.
const SCORE_TIER_VARIANT = {
  exceeds: 'success',
  meets: 'warning',
  improvement: 'caution',
  failure: 'critical',
}

export const ScorePill = ({ pct }) => {
  if (isNil(pct) || Number.isNaN(pct)) {
    return <Pill small bold content="—" />
  }
  const variantProps = { [SCORE_TIER_VARIANT[tierForPct(pct)]]: true }
  return <Pill small bold content={`${Math.round(pct)}%`} {...variantProps} />
}

// Outcome callouts appended after a measure/criterion. `skipped` and
// `n-a` are informational so they use the neutral default Pill.
const STATE_PILL_CONFIG = {
  'auto-fail': { label: 'Auto Fail', variant: 'critical' },
  'auto-zero': { label: 'Auto Zero', variant: 'caution' },
  skipped: { label: 'Skipped', variant: null },
  'n-a': { label: 'N/A', variant: null },
}

export const StatePill = ({ kind }) => {
  const config = STATE_PILL_CONFIG[kind]
  if (!config) {
    return null
  }
  const variantProps = config.variant ? { [config.variant]: true } : {}
  return <Pill xs bold className="uppercase" content={config.label} {...variantProps} />
}

export const TierBadge = ({ tier }) => {
  if (tier === 'above') {
    return <Pill xs bold success className="uppercase" content="Above" />
  }
  if (tier === 'below') {
    return <Pill xs bold critical className="uppercase" content="Below" />
  }
  return null
}
